import './ContentBuilderTemplate.scss';
import './ContentBuilderTemplate100.scss';

import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../../utils/bem';
import { usePropsHandler } from '../../EventInterceptor/usePropsHandler';
import { Typography } from '@dataartdev/uikit/Typography';
import { cnContentBuilderTemplate } from './index';
import { Slider, SliderItem } from '@dataartdev/uikit/Slider';
import { Button } from '@dataartdev/uikit/Button';
import { IconArrowRight } from '@dataartdev/uikit/IconArrowRight';
import { Picture } from '@dataartdev/uikit/Picture';
import { IRoot100 } from './models/CTI_100.props';
import { Swiper } from 'swiper';
import { prepareButtonAttrs } from '../../../utils/prepareButtonAttrs';
import { CtaScrollerBtn } from '../../CtaScrollerBtn';
import { useIntersectionObserver } from '../../../hooks/useIntersectionObserver';
import { useDispatch } from 'react-redux';
import { setContactButtonVisible } from '../../../../store';

const cnHeroCarousel = cn('HeroCarousel');
const animationDelay = 15;
const animationStyle = `progressBar ${animationDelay}s linear forwards`;
const sliderConfig = {
  watchOverflow: false,
  pagination: {
    el: '#swiper-pagination',
    type: 'progressbar',
    clickable: true,
  },
  // loop: true,
  effect: 'fade',
  fadeEffect: {
    crossFade: true,
  },
  watchSlidesProgress: true,
  autoplay: {
    delay: animationDelay * 1000,
    disableOnInteraction: false,
    // pauseOnMouseEnter: true,
  },
  slidesPerView: 1,
  spaceBetween: 0,
};

interface SwiperRef {
  swiper: Swiper;
}

export const ContentBuilderTemplate: React.FC<IRoot100> = props => {
  const { type, items, contentId } = usePropsHandler(
    cnContentBuilderTemplate(),
    props
  );
  const typeNumber = type.toString();

  const sliderRef = useRef<SwiperRef>(null);
  const [currentSlide, setcurrentSlide] = React.useState<number>(1);
  const [slider, setSlider] = React.useState<Swiper>();
  const dispatch = useDispatch();

  useEffect(() => {
    if (sliderRef.current) {
      setSlider(sliderRef.current.swiper);
    }
  }, [sliderRef]);

  useEffect(() => {
    if (slider) {
      slider.on('slideChange', () => {
        setcurrentSlide(slider.realIndex + 1);
      });
    }
  }, [slider]);

  const [useVideo, setUseVideo] = useState(true);

  useEffect(() => {
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntriesByName('first-contentful-paint')) {
        if (entry.startTime > 3000) {
          setUseVideo(false);
        }
      }
    });
    observer.observe({ type: 'paint', buffered: true });

    return () => observer.disconnect();
  }, []);

  const contactRefs = useRef<
    Array<React.RefObject<HTMLButtonElement | HTMLAnchorElement>>
  >(
    Array(items?.length)
      .fill(null)
      .map(() => React.createRef())
  );

  const [isVisible, isInitiallyChecked] = useIntersectionObserver(
    contactRefs.current[currentSlide - 1],
    {
      rootMargin: '0px',
      threshold: 1.0,
    },
    250
  );

  useEffect(() => {
    dispatch(setContactButtonVisible({ isVisible, isInitiallyChecked }));
  }, [isVisible, isInitiallyChecked]);

  const progressLine = useRef<Array<React.RefObject<HTMLDivElement>>>(
    Array(items?.length)
      .fill(null)
      .map(() => React.createRef())
  );
  useEffect(() => {
    if (!progressLine || !slider) {
      return;
    }
    slider?.on('slideChange', () => {
      const activeIndex = slider?.realIndex;
      progressLine.current?.forEach((bar, index) => {
        if (!bar || !bar.current) return;
        if (index === activeIndex) {
          bar.current.style.animation = animationStyle;
        } else {
          bar.current.style.animation = 'none';
        }
      });
    });

    const firstProgressBar = progressLine.current[0];
    if (firstProgressBar?.current) {
      firstProgressBar.current.style.animation = animationStyle;
    }
  }, [progressLine, slider]);

  const handlePrevClick = () => {
    if (slider && items) {
      if (slider.realIndex === 0) {
        slider.slideTo(items.length - 1, 0);
      } else {
        slider.slidePrev();
      }
    }
  };

  const handleNextClick = () => {
    if (slider && items) {
      if (slider.realIndex === items.length - 1) {
        slider.slideTo(0, 0);
      } else {
        slider.slideNext();
      }
    }
  };
  return (
    <div
      className={cnContentBuilderTemplate({ type: typeNumber })}
      id={contentId}
    >
      <div className={cnHeroCarousel()}>
        <Slider
          className={cnHeroCarousel('Slider')}
          {...sliderConfig}
          ref={sliderRef}
        >
          {items &&
            items.length > 0 &&
            items.map((slide, index) => {
              const { title, text, button, preview, video, id } = slide || {};
              const buttonAttr = prepareButtonAttrs(
                button?.link,
                button?.title,
                button?.targetBlank
              );
              return (
                <SliderItem className={cnHeroCarousel('Slide')} key={id}>
                  {video && useVideo ? (
                    <div className={cnHeroCarousel('Video')}>
                      <video autoPlay muted loop>
                        <source src={slide.video} type="video/mp4" />
                      </video>
                    </div>
                  ) : preview ? (
                    <Picture
                      className={cnHeroCarousel('Preview')}
                      {...slide?.preview}
                    />
                  ) : null}

                  <div className="container">
                    <div className={cnHeroCarousel('Inner')}>
                      {title && (
                        <Typography.Title
                          parse
                          size="hg"
                          as="h1"
                          className={cnHeroCarousel('Title')}
                        >
                          {title}
                        </Typography.Title>
                      )}
                      {text && (
                        <Typography.Text
                          parse
                          size="l"
                          as="p"
                          className={cnHeroCarousel('Text')}
                        >
                          {text}
                        </Typography.Text>
                      )}
                      {Object.keys(buttonAttr).length > 0 &&
                        ('isCTA' in buttonAttr ? (
                          <CtaScrollerBtn
                            className={cnHeroCarousel('Btn')}
                            view="secondary"
                            ref={contactRefs.current[index]}
                            {...buttonAttr}
                          />
                        ) : (
                          <Button
                            className={cnHeroCarousel('Btn')}
                            iconRight={IconArrowRight}
                            view="secondary"
                            ref={contactRefs.current[index]}
                            {...buttonAttr}
                          />
                        ))}
                    </div>
                    <div className={cnHeroCarousel('Progress-Line')}>
                      <div ref={progressLine.current[index]} />
                    </div>
                  </div>
                </SliderItem>
              );
            })}
        </Slider>
        <div className={cnHeroCarousel('Controls')}>
          <div className="container">
            <div className={cnHeroCarousel('Buttons')}>
              <button
                className={cnHeroCarousel('Switcher')}
                onClick={handlePrevClick}
              ></button>
              <button
                className={cnHeroCarousel('Switcher')}
                onClick={handleNextClick}
              ></button>
            </div>
            <div
              className={cnHeroCarousel('Slides')}
            >{`${currentSlide}/${items?.length}`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentBuilderTemplate;
